import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { UtilityCostDto } from '../paint-data-service';

interface UtilityCostCalculatorDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: (monthlyCost: number, costPerHour: number, utilityCosts: UtilityCostDto[]) => void;
  initialUtilityCosts: UtilityCostDto[];
  workHoursPerMonth: number;
  isNewVersion: boolean;
  systemSettingsUtilityCosts: string[];
  isLoadingSettings?: boolean;
}

const UtilityCostCalculatorDialog: React.FC<UtilityCostCalculatorDialogProps> = ({
  open,
  onClose,
  onAccept,
  initialUtilityCosts,
  workHoursPerMonth,
  isNewVersion,
  systemSettingsUtilityCosts,
  isLoadingSettings = false,
}) => {
  const [utilityCosts, setUtilityCosts] = useState<UtilityCostDto[]>([]);
  const [errors, setErrors] = useState<{ [key: number]: string }>({});
  /** Raw strings while typing so users can clear the field and replace values (controlled `0` blocks backspace). */
  const [monthlyCostInputByIndex, setMonthlyCostInputByIndex] = useState<Record<number, string>>(
    {}
  );

  // Sub-dialog state for adding a new utility cost type
  const [addCostOpen, setAddCostOpen] = useState(false);
  const [newCostLabel, setNewCostLabel] = useState('');
  const [newCostLabelError, setNewCostLabelError] = useState('');

  const parseMonthlyInputToNumber = (value: string): number => {
    const v = value.trim();
    if (v === '' || v === '.' || v === '-' || v === '-.') return 0;
    const n = parseFloat(v);
    return Number.isNaN(n) ? 0 : n;
  };

  const monthlyCostDisplayValue = (index: number, monthlyCost: number): string => {
    const raw = monthlyCostInputByIndex[index];
    if (raw !== undefined) return raw;
    return monthlyCost === 0 ? '' : String(monthlyCost);
  };

  useEffect(() => {
    if (open) {
      if (isNewVersion) {
        // For new version, always prioritize system settings types
        // Match costs from initialUtilityCosts if available
        const costMap = new Map(
          initialUtilityCosts.map(cost => [cost.utilityType.toLowerCase().trim(), cost.monthlyCost])
        );

        // Always use system settings if available, even if initialUtilityCosts has data
        if (systemSettingsUtilityCosts.length > 0) {
          // Use system settings types and match costs from latest version
          setUtilityCosts(
            systemSettingsUtilityCosts.map(type => ({
              utilityType: type,
              monthlyCost: costMap.get(type.toLowerCase().trim()) || 0,
            }))
          );
        } else if (initialUtilityCosts.length > 0) {
          // Fallback: use initialUtilityCosts if system settings not loaded yet
          // This happens when paintSettings hasn't loaded
          setUtilityCosts(initialUtilityCosts);
        } else {
          // Last resort: empty array (will show loading message)
          setUtilityCosts([]);
        }
      } else {
        // For editing existing, use saved data
        setUtilityCosts(
          initialUtilityCosts.length > 0
            ? initialUtilityCosts
            : [{ utilityType: '', monthlyCost: 0 }]
        );
      }
      setErrors({});
      setMonthlyCostInputByIndex({});
    }
  }, [open, isNewVersion, systemSettingsUtilityCosts, initialUtilityCosts]);

  const handleCostChange = (index: number, field: 'utilityType' | 'monthlyCost', value: string) => {
    const updated = [...utilityCosts];
    if (field === 'monthlyCost') {
      setMonthlyCostInputByIndex(prev => ({ ...prev, [index]: value }));
      updated[index] = {
        ...updated[index],
        monthlyCost: parseMonthlyInputToNumber(value),
      };
    } else {
      updated[index] = { ...updated[index], utilityType: value };
    }
    setUtilityCosts(updated);

    // Clear error for this field
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const handleAddRow = () => {
    setUtilityCosts([...utilityCosts, { utilityType: '', monthlyCost: 0 }]);
  };

  const handleRemoveRow = (index: number) => {
    const updated = utilityCosts.filter((_, i) => i !== index);
    setUtilityCosts(updated);
    setMonthlyCostInputByIndex({});
    setErrors({});
  };

  const calculateTotals = () => {
    const monthlyTotal = utilityCosts.reduce((sum, cost) => sum + cost.monthlyCost, 0);
    const costPerHour = workHoursPerMonth > 0 ? monthlyTotal / workHoursPerMonth : 0;
    return { monthlyTotal, costPerHour };
  };

  const handleAccept = () => {
    // Validate
    const validationErrors: { [key: number]: string } = {};
    utilityCosts.forEach((cost, index) => {
      if (cost.monthlyCost < 0) {
        validationErrors[index] = 'Monthly cost must be greater than or equal to zero';
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Warn if work hours per month is not set
    if (workHoursPerMonth <= 0) {
      setErrors({
        [-1]: 'Work Hours per Month must be entered in the main form to calculate cost per hour.',
      });
      return;
    }

    const { monthlyTotal, costPerHour } = calculateTotals();
    onAccept(monthlyTotal, costPerHour, utilityCosts);
  };

  // Sub-dialog handlers
  const handleOpenAddCost = () => {
    setNewCostLabel('');
    setNewCostLabelError('');
    setAddCostOpen(true);
  };

  const handleCloseAddCost = () => {
    setAddCostOpen(false);
    setNewCostLabel('');
    setNewCostLabelError('');
  };

  const handleSaveNewCost = () => {
    const trimmed = newCostLabel.trim();
    if (!trimmed) {
      setNewCostLabelError('Cost name is required');
      return;
    }
    if (trimmed.length > 100) {
      setNewCostLabelError('Cost name must not exceed 100 characters');
      return;
    }
    setUtilityCosts(prev => [...prev, { utilityType: trimmed, monthlyCost: 0 }]);
    handleCloseAddCost();
  };

  const { monthlyTotal, costPerHour } = calculateTotals();

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Calculate Utility Costs per Hour</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter the following monthly utility costs as applicable.
          </Typography>

          <Box sx={{ mb: 2 }}>
            {utilityCosts.length === 0 && isNewVersion ? (
              <Typography variant="body2" color="text.secondary">
                {isLoadingSettings
                  ? 'Loading utility cost types from system settings...'
                  : 'No utility cost types configured. Click "+ Utility Cost" below to add one.'}
              </Typography>
            ) : (
              utilityCosts.map((cost, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {isNewVersion ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, fontSize: '0.95rem', wordBreak: 'break-word' }}
                      >
                        {cost.utilityType}:
                      </Typography>
                      <TextField
                        type="text"
                        inputMode="decimal"
                        value={monthlyCostDisplayValue(index, cost.monthlyCost)}
                        onChange={e => handleCostChange(index, 'monthlyCost', e.target.value)}
                        error={!!errors[index]}
                        helperText={errors[index]}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ maxWidth: 220 }}
                      />
                    </Box>
                  ) : (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          label="Utility Type"
                          value={cost.utilityType}
                          onChange={e => handleCostChange(index, 'utilityType', e.target.value)}
                          error={!!errors[index]}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Monthly Cost"
                          type="text"
                          inputMode="decimal"
                          value={monthlyCostDisplayValue(index, cost.monthlyCost)}
                          onChange={e => handleCostChange(index, 'monthlyCost', e.target.value)}
                          error={!!errors[index]}
                          helperText={errors[index]}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <Button
                          onClick={() => handleRemoveRow(index)}
                          color="error"
                          size="small"
                          disabled={utilityCosts.length === 1}
                        >
                          ×
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              ))
            )}
          </Box>

          {errors[-1] && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {errors[-1]}
            </Typography>
          )}

          {!isNewVersion && (
            <Button onClick={handleAddRow} variant="outlined" size="small" sx={{ mb: 2 }}>
              Add Utility Cost
            </Button>
          )}

          <Divider sx={{ my: 2 }} />

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Monthly Cost:
                </Typography>
                <Typography variant="h6">${monthlyTotal.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Cost per Hour:
                </Typography>
                <Typography variant="h6">
                  {workHoursPerMonth > 0 ? `$${costPerHour.toFixed(2)}` : 'Enter Work Hours'}
                </Typography>
              </Grid>
            </Grid>
            {workHoursPerMonth > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Approximately {workHoursPerMonth} hours a month ~ ${monthlyTotal.toFixed(2)} /{' '}
                {workHoursPerMonth} = ${costPerHour.toFixed(2)}
              </Typography>
            ) : (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                Please enter "Work Hours per Month" in the main form to calculate cost per hour.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {isNewVersion && (
              <Button onClick={handleOpenAddCost} variant="outlined" size="small">
                + Utility Cost
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={handleAccept} variant="contained" color="primary">
              Accept
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Sub-dialog: Add new utility cost type */}
      <Dialog open={addCostOpen} onClose={handleCloseAddCost} maxWidth="xs" fullWidth>
        <DialogTitle>Add Utility Cost</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a name for the new utility cost. You can enter the dollar amount after adding it.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Utility Cost Name"
            value={newCostLabel}
            onChange={e => {
              setNewCostLabel(e.target.value);
              if (newCostLabelError) setNewCostLabelError('');
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSaveNewCost();
            }}
            error={!!newCostLabelError}
            helperText={newCostLabelError}
            inputProps={{ maxLength: 100 }}
            placeholder="e.g. Electricity"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddCost}>Cancel</Button>
          <Button onClick={handleSaveNewCost} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UtilityCostCalculatorDialog;
